/**
 * Info 폼 이벤트 핸들러 공통 훅
 * 헤더 정보 입력 폼의 코드 검색 이벤트 처리 통합
 */

import { useCallback, useMemo } from "react";
import { 
	UseInfoFormEventsProps, 
	InfoFieldHandlers, 
	InfoCodeSearchFieldConfig 
} from "src/types/info-form";
import { getCategoryByTitle } from "src/configs/categories";
import { validateString } from "src/utils/string/stringUtil";

export function useInfoFormEvents<T>({
	editData,
	setEditData,
	defaultData,
	infoFieldConfigs,
	setCodeSearchModalProps,
	formName,
}: UseInfoFormEventsProps<T>) {


	/**
	 * 범용 엔터키 핸들러 생성
	 * 
	 * 동작 방식:
	 * 1. API 호출하여 검색 결과 확인
	 * 2. 결과가 정확히 1개 → 자동완성 (모달 안 열림)
	 * 3. 결과가 0개 또는 2개 이상 → 모달 열기 (수동 검색)
	 */
	const createEnterHandler = useCallback((config: InfoCodeSearchFieldConfig) => 
		async (e: React.KeyboardEvent<HTMLInputElement>) => {
			if(!e.currentTarget.value || !validateString(e.currentTarget.value)) {
				return;
			}
			if (e.key === 'Enter') {
				// 현재 입력 값 가져오기
				const fieldValue = (e.currentTarget as HTMLInputElement).value;
				
				// extraFilter 처리 (config에서 가져오거나 함수 실행)
				const extraFilter = typeof config.extraFilter === 'function' 
					? config.extraFilter() 
					: config.extraFilter;
				
				// 카테고리 설정 가져오기
				const categoryConfig = getCategoryByTitle(config.category as string);
				
				// 1단계: 입력값이 있으면 API 호출하여 자동완성 시도
				if (categoryConfig && fieldValue) {
					try {
						const apiParams = categoryConfig.getApiParams(fieldValue, extraFilter || {});
						const response = await categoryConfig.apiFunction(...apiParams);
						const results = response?.data?.data || [];
						
						// 결과가 정확히 1개면 자동완성
						if (results.length === 1) {
							const item = results[0];
							const updatedFields = Object.entries(config.responseMapping).reduce((acc, [responseKey, targetField]) => ({
								...acc,
								[targetField]: item[responseKey] || ''
							}), {});
							
							setEditData({
								...defaultData,
								...editData,
								...updatedFields
							});
							config.onAfterConfirm?.(updatedFields);
							return;  // 자동완성 완료, 모달 안 열림
						}
					} catch (error) {
						console.warn('Auto-complete failed:', error);
						// 실패하면 아래로 진행하여 모달 열기
					}
				}
				
				// 2단계: 결과가 0개 또는 2개 이상이면 모달 열기
				setCodeSearchModalProps({
					category: config.category,
					search: fieldValue || '',
					formName: formName,
					isInfo: true,
					handleFoucsId: config.focusId,
					extraFilter: extraFilter,
					preserveExtraFilterOnReset: config.preserveExtraFilterOnReset,
					wrapperOption: {
						size: categoryConfig?.modalSize?.size || 'md',
						defaultWidth: categoryConfig?.modalSize?.width || 70
					},
					onConfirm: (data: any) => {
						if (!editData) return;
						
						// 응답 데이터 매핑
						const updatedFields = Object.entries(config.responseMapping).reduce((acc, [responseKey, targetField]) => ({
							...acc,
							[targetField]: data[responseKey] || ''
						}), {});
						
						setEditData({
							...defaultData,
							...editData,
							...updatedFields
						});
						config.onAfterConfirm?.(updatedFields);
					},
					onClear: () => {
						if (!editData) return;
						
						// 관련 필드들 초기화
						const clearedFields = Object.values(config.responseMapping).reduce((acc, targetField) => ({
							...acc,
							[targetField]: ''
						}), {});
						
						setEditData({
							...defaultData,
							...editData,
							...clearedFields
						});
					}
				});
			}
		}, [editData, setEditData, defaultData, setCodeSearchModalProps, formName]);

	/**
	 * 범용 더블클릭 핸들러 생성
	 */
	const createDoubleClickHandler = useCallback((config: InfoCodeSearchFieldConfig) => 
		async (e: React.MouseEvent<HTMLInputElement>) => {
			e.currentTarget.blur();
			
			// 현재 입력 값 가져오기
			const fieldValue = (e.currentTarget as HTMLInputElement).value;
			
			// extraFilter 처리 (config에서 가져오거나 함수 실행)
			const extraFilter = typeof config.extraFilter === 'function' 
				? config.extraFilter() 
				: config.extraFilter;
			
			// 모달 열기
			const categoryConfig = getCategoryByTitle(config.category as string);
			setCodeSearchModalProps({
				category: config.category,
				search: fieldValue || '',
				formName: formName,
				isInfo: true,
				handleFoucsId: config.focusId,
				extraFilter: extraFilter,  //    extraFilter 전달
				preserveExtraFilterOnReset: config.preserveExtraFilterOnReset,
				wrapperOption: {
					size: categoryConfig?.modalSize?.size || 'md',
					defaultWidth: categoryConfig?.modalSize?.width || 70
				},
				onConfirm: (data: any) => {
					if (!editData) return;
					
					// 응답 데이터 매핑
					const updatedFields = Object.entries(config.responseMapping).reduce((acc, [responseKey, targetField]) => ({
						...acc,
						[targetField]: data[responseKey] || ''
					}), {});
					
					setEditData({
						...defaultData,
						...editData,
						...updatedFields
					});
					config.onAfterConfirm?.(updatedFields);
				},
				onClear: () => {
					if (!editData) return;
					
					// 관련 필드들 초기화
					const clearedFields = Object.values(config.responseMapping).reduce((acc, targetField) => ({
						...acc,
						[targetField]: ''
					}), {});
					
					setEditData({
						...defaultData,
						...editData,
						...clearedFields
					});
				}
			});
		}, [editData, setEditData, defaultData, setCodeSearchModalProps, formName]);

	/**
	 * 연관된 필드(주로 명칭) 초기화 객체 생성
	 */
	const getRelatedFieldsClear = useCallback((config: InfoCodeSearchFieldConfig) => {
		return Object.values(config.responseMapping).reduce((acc, targetField) => {
			// 현재 입력하고 있는 필드(Code)는 제외하고 나머지만 초기화
			if (targetField !== config.fieldName) {
				return { ...acc, [targetField]: '' };
			}
			return acc;
		}, {});
	}, []);

	/**
	 * 범용 onChange 핸들러 생성
	 * 코드가 변경되면 연관된 명칭 필드도 함께 초기화
	 */
	const createChangeHandler = useCallback((config: InfoCodeSearchFieldConfig) => 
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (!editData) return;

			// 연관된 필드(주로 명칭) 초기화
			const clearedFields = getRelatedFieldsClear(config);

			setEditData({
				...editData,
				...clearedFields,
				[config.fieldName]: e.target.value
			});
		}, [editData, setEditData, getRelatedFieldsClear]);

	/**
	 * 각 필드별 핸들러들 자동 생성
	 */
	const fieldHandlers = useMemo(() => {
		//useMemo 기반 함수, 정적 배열일때의 처리
		return infoFieldConfigs.reduce((acc, config) => ({
			...acc,
			[config.fieldName]: {
				onChange: createChangeHandler(config),
				onEnterKeyDown: createEnterHandler(config),
				onDoubleClick: createDoubleClickHandler(config)
			} as InfoFieldHandlers
		}), {} as Record<string, InfoFieldHandlers>);

		
	}, [infoFieldConfigs, createEnterHandler, createDoubleClickHandler, createChangeHandler]);

	/**
	 * 특정 필드의 핸들러 가져오기 (안전한 접근)
	 */
	const getFieldHandlers = useCallback((fieldName: string): InfoFieldHandlers => {
		return fieldHandlers[fieldName] || {
			onChange: () => {},
			onEnterKeyDown: () => {},
			onDoubleClick: () => {}
		};
	}, [fieldHandlers]);

	return {
		fieldHandlers,
		getFieldHandlers
	};
}
